import { formatearMoneda } from '@plataforma/utils';

import { agregarProducto } from '../carrito.js';
import {
  escaparHtml,
  enlaceWhatsApp,
  etiquetasProducto,
  imagenPrincipal,
  tarjetaProductoHtml,
} from '../componentes.js';
import { cargarDetalleProducto } from '../datos-publicados.js';
import { iconoLucide } from '../iconos.js';
import { iconoRedSocial } from '../redes-sociales.js';

function obtenerImagenes(producto) {
  const principal = imagenPrincipal(producto);
  const imagenes = producto.imagenes?.length ? producto.imagenes : [principal];
  const urls = new Set();

  return imagenes.filter((imagen) => {
    if (!imagen?.url || urls.has(imagen.url)) return false;
    urls.add(imagen.url);
    return true;
  });
}

function obtenerVariantesVisibles(producto) {
  return (producto.variantes ?? []).filter((variante) => variante.estado !== 'oculta');
}

function obtenerGruposAtributos(variantes) {
  const grupos = new Map();

  variantes.forEach((variante) => {
    (variante.valores ?? []).forEach((valor) => {
      const atributoId = valor.atributo_id ?? valor.atributo?.id ?? valor.atributo_nombre;
      if (!atributoId) return;

      if (!grupos.has(atributoId)) {
        grupos.set(atributoId, {
          id: atributoId,
          nombre: valor.atributo_nombre ?? valor.atributo?.nombre ?? 'Opcion',
          tipo_visualizacion:
            valor.tipo_visualizacion ?? valor.atributo?.tipo_visualizacion ?? 'texto',
          valores: new Map(),
        });
      }

      const grupo = grupos.get(atributoId);
      const valorId = valor.valor_id ?? valor.id ?? valor.valor;
      if (!grupo.valores.has(valorId)) {
        grupo.valores.set(valorId, {
          id: valorId,
          valor: valor.valor,
          valor_color_hex: valor.valor_color_hex,
          valor_imagen_url: valor.valor_imagen_url,
          orden: Number(valor.orden ?? 0),
        });
      }
    });
  });

  return Array.from(grupos.values()).map((grupo) => ({
    ...grupo,
    valores: Array.from(grupo.valores.values()).sort((a, b) => a.orden - b.orden),
  }));
}

function construirSeleccionInicial(grupos, varianteInicial) {
  const seleccion = {};

  grupos.forEach((grupo) => {
    const valorVariante = varianteInicial?.valores?.find(
      (valor) =>
        String(valor.atributo_id ?? valor.atributo?.id ?? valor.atributo_nombre) ===
        String(grupo.id),
    );
    seleccion[grupo.id] = String(
      valorVariante?.valor_id ??
        valorVariante?.id ??
        valorVariante?.valor ??
        grupo.valores[0]?.id ??
        '',
    );
  });

  return seleccion;
}

function encontrarVariante(variantes, seleccion, grupos) {
  if (!grupos.length) return null;

  return (
    variantes.find((variante) =>
      grupos.every((grupo) =>
        variante.valores?.some(
          (valor) =>
            String(valor.atributo_id ?? valor.atributo?.id ?? valor.atributo_nombre) ===
              String(grupo.id) &&
            String(valor.valor_id ?? valor.id ?? valor.valor) === String(seleccion[grupo.id]),
        ),
      ),
    ) ?? null
  );
}

function atributosDesdeVariante(variante) {
  return Object.fromEntries(
    (variante?.valores ?? []).map((valor) => [
      valor.atributo_nombre ?? valor.atributo?.nombre ?? 'Opcion',
      valor.valor,
    ]),
  );
}

function renderSelectorVariantes(grupos, seleccion) {
  if (!grupos.length) return '';

  return `
    <div class="sp-variantes" aria-label="Opciones del producto">
      ${grupos
        .map(
          (grupo) => `
            <fieldset class="sp-variante-grupo">
              <legend>${escaparHtml(grupo.nombre)}</legend>
              <div class="sp-variante-opciones">
                ${grupo.valores
                  .map((valor) => {
                    const activo = String(seleccion[grupo.id]) === String(valor.id);
                    const contenido =
                      grupo.tipo_visualizacion === 'color' && valor.valor_color_hex
                        ? `<span class="sp-swatch" style="background:${escaparHtml(valor.valor_color_hex)}"></span><span>${escaparHtml(valor.valor)}</span>`
                        : grupo.tipo_visualizacion === 'imagen' && valor.valor_imagen_url
                          ? `<img src="${escaparHtml(valor.valor_imagen_url)}" alt="" loading="lazy" decoding="async" /><span>${escaparHtml(valor.valor)}</span>`
                          : escaparHtml(valor.valor);

                    return `
                      <button
                        type="button"
                        class="sp-variante-opcion ${activo ? 'sp-variante-opcion--activa' : ''}"
                        data-atributo-id="${escaparHtml(grupo.id)}"
                        data-valor-id="${escaparHtml(valor.id)}"
                        aria-pressed="${activo ? 'true' : 'false'}"
                      >${contenido}</button>
                    `;
                  })
                  .join('')}
              </div>
            </fieldset>
          `,
        )
        .join('')}
    </div>
  `;
}

export function vistaProducto(contenedor, tienda, slugProducto) {
  const producto = tienda.productos.find((item) => item.slug === slugProducto);

  // Marca que producto esta actualmente mostrado en este contenedor. Se usa
  // como guarda para el fetch de detalle en segundo plano: si el usuario ya
  // navego a otra pagina cuando la respuesta llega, no se debe re-renderizar
  // encima de una vista distinta.
  contenedor.dataset.productoActual = slugProducto;

  if (!producto) {
    contenedor.innerHTML = `
      <section class="sp-page sp-contenedor sp-vacio">
        <h1>Producto no encontrado</h1>
        <p>Puede que ya no este publicado o que el enlace haya cambiado.</p>
        <a class="sp-boton sp-boton--secundario" href="/catalogo">Volver al catalogo</a>
      </section>
    `;
    return;
  }

  const imagenes = obtenerImagenes(producto);
  let indiceImagenActiva = 0;
  const categoriaPrincipal = producto.categorias?.[0];
  const variantes = obtenerVariantesVisibles(producto);
  const grupos = obtenerGruposAtributos(variantes);
  const seleccion = construirSeleccionInicial(grupos, variantes[0]);

  let relacionados;
  if (Array.isArray(producto.relacionados) && producto.relacionados.length > 0) {
    const slugsRel = new Set(producto.relacionados);
    relacionados = tienda.productos.filter((item) => slugsRel.has(item.slug));
  } else {
    relacionados = tienda.productos
      .filter(
        (item) =>
          item.slug !== producto.slug &&
          categoriaPrincipal &&
          item.categorias?.some((categoria) => categoria.slug === categoriaPrincipal.slug),
      )
      .slice(0, 4);
  }

  async function completarRelacionadosFaltantes() {
    if (!Array.isArray(producto.relacionados) || producto.relacionados.length === 0) {
      return relacionados;
    }

    const slugsFaltantes = producto.relacionados.filter(
      (slug) => !relacionados.some((item) => item.slug === slug),
    );

    if (slugsFaltantes.length === 0) return relacionados;

    const detalles = await Promise.all(
      slugsFaltantes.map((slug) => cargarDetalleProducto(tienda.slugTienda, slug)),
    );

    const encontrados = detalles.filter(Boolean);
    relacionados = [...relacionados, ...encontrados];
    return relacionados;
  }

  function renderizarDetalle() {
    const varianteSeleccionada = encontrarVariante(variantes, seleccion, grupos);
    const precioActual = Number(varianteSeleccionada?.precio ?? producto.precio);
    const imagenPrincipalActual = varianteSeleccionada?.imagen_url
      ? { url: varianteSeleccionada.imagen_url, texto_alternativo: producto.nombre }
      : (imagenes[indiceImagenActiva] ?? imagenes[0]);
    const textoConsulta = varianteSeleccionada
      ? `Hola, quiero consultar por ${producto.nombre} (${Object.entries(
          atributosDesdeVariante(varianteSeleccionada),
        )
          .map(([clave, valor]) => `${clave}: ${valor}`)
          .join(', ')})`
      : `Hola, quiero consultar por ${producto.nombre}`;

    contenedor.innerHTML = `
      <section class="sp-page sp-contenedor">
        <nav class="sp-breadcrumb" aria-label="Ruta">
          <a href="/">Inicio</a>
          <span class="sp-breadcrumb__sep" aria-hidden="true">${iconoLucide('ChevronRight')}</span>
          <a href="/catalogo">Catalogo</a>
          ${
            categoriaPrincipal
              ? `<span class="sp-breadcrumb__sep" aria-hidden="true">${iconoLucide('ChevronRight')}</span><a href="/catalogo?categoria=${escaparHtml(categoriaPrincipal.slug)}">${escaparHtml(categoriaPrincipal.nombre)}</a>`
              : ''
          }
        </nav>

        <article class="sp-detalle">
          <div class="sp-galeria">
            <div class="sp-galeria__visor">
              <img class="sp-galeria__principal" src="${escaparHtml(imagenPrincipalActual.url)}" alt="${escaparHtml(imagenPrincipalActual.texto_alternativo ?? producto.nombre)}" fetchpriority="high" decoding="async" />
              <button class="sp-galeria__zoom" type="button" data-zoom-imagen aria-label="Ampliar imagen">
                ${iconoLucide('Eye')}
              </button>
            </div>
            <div class="sp-galeria__miniaturas">
              ${imagenes
                .map(
                  (imagen, index) => `
                    <button type="button" class="${index === indiceImagenActiva ? 'sp-miniatura-activa' : ''}" data-imagen="${escaparHtml(imagen.url)}" data-imagen-indice="${index}" aria-label="Ver imagen ${index + 1}">
                      <img src="${escaparHtml(imagen.url)}" alt="${escaparHtml(imagen.texto_alternativo ?? producto.nombre)}" loading="lazy" decoding="async" />
                    </button>`,
                )
                .join('')}
            </div>
          </div>

          <div class="sp-detalle__info">
            <p class="sp-eyebrow">${escaparHtml(categoriaPrincipal?.nombre ?? 'Producto')}</p>
            <h1>${escaparHtml(producto.nombre)}</h1>
            <div class="sp-producto__chips">
              ${etiquetasProducto(producto)
                .map((etiqueta) => `<span class="sp-chip">${escaparHtml(etiqueta)}</span>`)
                .join('')}
            </div>
            <p>${escaparHtml(producto.descripcion_corta ?? '')}</p>
            <div class="sp-detalle__precio">
              <strong>${formatearMoneda(precioActual, tienda.empresa.moneda)}</strong>
              ${
                producto.precio_anterior
                  ? `<span>${formatearMoneda(Number(producto.precio_anterior), tienda.empresa.moneda)}</span>`
                  : ''
              }
            </div>
            ${
              typeof producto.stock === 'number' || varianteSeleccionada?.sku || producto.sku
                ? `<div class="sp-detalle__meta">
                    ${
                      typeof producto.stock === 'number'
                        ? producto.stock > 0
                          ? `<p class="sp-detalle__stock sp-detalle__stock--ok">${iconoLucide('Check')}${producto.stock} disponible${producto.stock === 1 ? '' : 's'}</p>`
                          : `<p class="sp-detalle__stock sp-detalle__stock--agotado">${iconoLucide('Ban')}Agotado</p>`
                        : ''
                    }
                    ${
                      varianteSeleccionada?.sku || producto.sku
                        ? `<p class="sp-detalle__sku">SKU: ${escaparHtml(String(varianteSeleccionada?.sku ?? producto.sku))}</p>`
                        : ''
                    }
                  </div>`
                : ''
            }
            ${renderSelectorVariantes(grupos, seleccion)}
            ${
              grupos.length && !varianteSeleccionada
                ? '<p class="sp-campo-ayuda">Esta combinacion no esta disponible.</p>'
                : ''
            }
            <div class="sp-acciones sp-acciones--detalle">
              <button class="sp-boton sp-boton--primario" type="button" id="sp-agregar-detalle" ${
                grupos.length && !varianteSeleccionada ? 'disabled' : ''
              }>${iconoLucide('ShoppingCart')}<span>Agregar al carrito</span></button>
              <a class="sp-boton sp-boton--secundario" href="${enlaceWhatsApp(tienda, textoConsulta)}" target="_blank" rel="noreferrer">${iconoRedSocial('whatsapp')}<span>Consultar por WhatsApp</span></a>
            </div>
            <div class="sp-acciones-sticky">
              <button class="sp-boton sp-boton--primario" type="button" id="sp-agregar-sticky" ${
                grupos.length && !varianteSeleccionada ? 'disabled' : ''
              }>${iconoLucide('ShoppingCart')}<span>Agregar</span></button>
              <a class="sp-boton sp-boton--secundario" href="${enlaceWhatsApp(tienda, textoConsulta)}" target="_blank" rel="noreferrer">${iconoRedSocial('whatsapp')}<span>WhatsApp</span></a>
            </div>
            ${
              producto.descripcion_larga
                ? `<div class="sp-descripcion-larga"><h2>Detalles</h2><p>${escaparHtml(producto.descripcion_larga)}</p></div>`
                : ''
            }
          </div>
        </article>

        ${
          relacionados.length
            ? `<section class="sp-seccion">
                <div class="sp-seccion__cabecera">
                  <div>
                    <p class="sp-eyebrow">Tambien puede interesarte</p>
                    <h2>Productos relacionados</h2>
                  </div>
                </div>
                <div class="sp-grid-productos">${relacionados
                  .map((item) => tarjetaProductoHtml(item, tienda.empresa.moneda))
                  .join('')}</div>
              </section>`
            : ''
        }
      </section>
    `;

    contenedor.querySelectorAll('[data-imagen]').forEach((boton) => {
      boton.addEventListener('click', () => {
        indiceImagenActiva = Number(boton.dataset.imagenIndice ?? 0);
        contenedor.querySelector('.sp-galeria__principal').src = boton.dataset.imagen;
        contenedor
          .querySelectorAll('[data-imagen]')
          .forEach((item) => item.classList.toggle('sp-miniatura-activa', item === boton));
      });
    });

    const zoom = contenedor.querySelector('[data-zoom-imagen]');
    zoom?.addEventListener('click', () => {
      zoom.classList.toggle('sp-galeria__zoom--activo');
    });

    let toqueInicio = 0;
    zoom?.addEventListener(
      'touchstart',
      (evento) => {
        toqueInicio = evento.changedTouches[0].clientX;
      },
      { passive: true },
    );
    zoom?.addEventListener(
      'touchend',
      (evento) => {
        const delta = evento.changedTouches[0].clientX - toqueInicio;
        if (Math.abs(delta) < 36 || imagenes.length <= 1) return;
        indiceImagenActiva =
          (indiceImagenActiva + (delta < 0 ? 1 : -1) + imagenes.length) % imagenes.length;
        renderizarDetalle();
      },
      { passive: true },
    );

    contenedor.querySelectorAll('[data-atributo-id]').forEach((boton) => {
      boton.addEventListener('click', () => {
        seleccion[boton.dataset.atributoId] = boton.dataset.valorId;
        renderizarDetalle();
      });
    });

    const agregarSeleccion = (boton) => {
      const variante = encontrarVariante(variantes, seleccion, grupos);
      const atributos = atributosDesdeVariante(variante);
      agregarProducto(
        producto.slug,
        1,
        variante?.id ?? null,
        Object.keys(atributos).length ? atributos : null,
      );
      boton.classList.add('sp-boton--ok');
      boton.innerHTML = `${iconoLucide('ShoppingBag')}<span>Agregado</span>`;
      window.setTimeout(() => {
        boton.classList.remove('sp-boton--ok');
        boton.innerHTML =
          boton.id === 'sp-agregar-sticky'
            ? `${iconoLucide('ShoppingCart')}<span>Agregar</span>`
            : `${iconoLucide('ShoppingCart')}<span>Agregar al carrito</span>`;
      }, 900);
    };

    contenedor.querySelector('#sp-agregar-detalle')?.addEventListener('click', (evento) => {
      agregarSeleccion(evento.currentTarget);
    });
    contenedor.querySelector('#sp-agregar-sticky')?.addEventListener('click', (evento) => {
      agregarSeleccion(evento.currentTarget);
    });
  }

  renderizarDetalle();

  const cantidadRelacionadosInicial = relacionados.length;

  completarRelacionadosFaltantes().then((relacionadosCompletos) => {
    if (contenedor.dataset.productoActual !== slugProducto) return;
    if (relacionadosCompletos.length > cantidadRelacionadosInicial) {
      relacionados = relacionadosCompletos;
      renderizarDetalle();
    }
  });

  if (!producto._detalleCompleto) {
    cargarDetalleProducto(tienda.slugTienda, slugProducto).then((detalle) => {
      if (!detalle) return;
      if (contenedor.dataset.productoActual !== slugProducto) return;

      const tiendaConDetalle = {
        ...tienda,
        productos: tienda.productos.map((item) =>
          item.slug === slugProducto ? { ...item, ...detalle, _detalleCompleto: true } : item,
        ),
      };
      vistaProducto(contenedor, tiendaConDetalle, slugProducto);
    });
  }
}
